import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MousePointerClick } from 'lucide-react';

/* ── Types ─────────────────────────────────────────────────────── */
export interface DemoKeyframe {
  /** Where the ghost cursor moves (absolute px or CSS selector) */
  x: number;
  y: number;
  /** Duration of travel in ms */
  duration?: number;
  /** Action at this keyframe */
  action?: 'click' | 'drag-start' | 'drag-end' | 'hover' | 'pause';
  /** Label shown near cursor */
  label?: string;
}

export interface DemoSequence {
  keyframes: DemoKeyframe[];
  /** Loop the demo */
  loop?: boolean;
}

/* ── Helpers ───────────────────────────────────────────────────── */
function resolvePosition(kf: DemoKeyframe, targetRect: DOMRect | null): { x: number; y: number } {
  return { x: kf.x, y: kf.y };
}

/* ── Component ─────────────────────────────────────────────────── */
interface Props {
  sequence: DemoSequence;
  targetRect: DOMRect | null;
  active: boolean;
}

export function TutorialAnimatedDemo({ sequence, targetRect, active }: Props) {
  const [step, setStep] = useState(0);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [action, setAction] = useState<DemoKeyframe['action']>(undefined);
  const [dragTrail, setDragTrail] = useState<{ x: number; y: number }[]>([]);
  const [clickRipple, setClickRipple] = useState<{ x: number; y: number; id: number } | null>(null);
  const [label, setLabel] = useState<string | undefined>();
  const [playing, setPlaying] = useState(false);

  const kfs = sequence.keyframes;

  const play = useCallback(() => {
    if (!active || kfs.length === 0) return;
    setStep(0);
    setDragTrail([]);
    setPlaying(true);
    const start = resolvePosition(kfs[0], targetRect);
    setPos(start);
    setAction(undefined);
    setLabel(undefined);
  }, [active, kfs, targetRect]);

  // Auto-start and loop
  useEffect(() => {
    if (active) play();
  }, [active, play]);

  // Step through keyframes
  useEffect(() => {
    if (!playing || step >= kfs.length) {
      if (playing && sequence.loop) {
        const timer = setTimeout(play, 1200);
        return () => clearTimeout(timer);
      }
      setPlaying(false);
      return;
    }

    const kf = kfs[step];
    const target = resolvePosition(kf, targetRect);
    const dur = kf.duration ?? 600;

    // Start moving
    setPos(target);
    setLabel(kf.label);

    // If dragging, build trail
    if (kf.action === 'drag-start') {
      setDragTrail([target]);
      setAction('drag-start');
    } else if (kf.action === 'drag-end') {
      setDragTrail(prev => [...prev, target]);
      setAction('drag-end');
    } else if (action === 'drag-start') {
      // Mid-drag
      setDragTrail(prev => [...prev, target]);
    }

    // Click ripple
    if (kf.action === 'click') {
      const timer = setTimeout(() => {
        setClickRipple({ x: target.x, y: target.y, id: Date.now() });
        setAction('click');
      }, dur);
      const next = setTimeout(() => {
        setAction(undefined);
        setStep(s => s + 1);
      }, dur + 400);
      return () => { clearTimeout(timer); clearTimeout(next); };
    }

    if (kf.action === 'pause') {
      setAction('pause');
      const timer = setTimeout(() => {
        setAction(undefined);
        setStep(s => s + 1);
      }, dur + 800);
      return () => clearTimeout(timer);
    }

    // Default: move then advance
    const timer = setTimeout(() => {
      if (kf.action === 'drag-end') {
        setTimeout(() => setDragTrail([]), 300);
      }
      setAction(kf.action);
      setStep(s => s + 1);
    }, dur);
    return () => clearTimeout(timer);
  }, [step, playing, kfs, targetRect, sequence.loop, play, action]);

  if (!active || kfs.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[10001] pointer-events-none">
      {/* Drag trail line */}
      {dragTrail.length > 1 && (
        <svg className="absolute inset-0 w-full h-full">
          <motion.path
            d={dragTrail.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')}
            stroke="hsl(var(--primary))"
            strokeWidth={3}
            strokeDasharray="8 4"
            fill="none"
            initial={{ pathLength: 0, opacity: 0.4 }}
            animate={{ pathLength: 1, opacity: 0.8 }}
            transition={{ duration: 0.4 }}
          />
          {/* Drag start dot */}
          <circle cx={dragTrail[0].x} cy={dragTrail[0].y} r={6} fill="hsl(var(--primary))" opacity={0.5} />
          {/* Drag end dot */}
          {dragTrail.length > 1 && action === 'drag-end' && (
            <motion.circle
              cx={dragTrail[dragTrail.length - 1].x}
              cy={dragTrail[dragTrail.length - 1].y}
              r={8}
              fill="hsl(var(--primary))"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 10 }}
            />
          )}
        </svg>
      )}

      {/* Click ripple */}
      <AnimatePresence>
        {clickRipple && (
          <motion.div
            key={clickRipple.id}
            className="absolute rounded-full border-2 border-primary"
            style={{ left: clickRipple.x - 20, top: clickRipple.y - 20, width: 40, height: 40 }}
            initial={{ scale: 0.5, opacity: 1 }}
            animate={{ scale: 2.5, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          />
        )}
      </AnimatePresence>

      {/* Ghost cursor */}
      <motion.div
        className="absolute"
        animate={{ x: pos.x - 6, y: pos.y - 2 }}
        transition={{ type: 'spring', damping: 25, stiffness: 120 }}
      >
        {/* Cursor icon */}
        <div className="relative">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="drop-shadow-lg">
            <path
              d="M5 3l14 8.5-6.5 1.5-3.5 6z"
              fill="hsl(var(--primary))"
              stroke="hsl(var(--primary-foreground))"
              strokeWidth={1.5}
            />
          </svg>
          {/* Click indicator */}
          {action === 'click' && (
            <motion.div
              className="absolute -top-1 -left-1 w-6 h-6 rounded-full bg-primary/30"
              initial={{ scale: 1 }}
              animate={{ scale: 0 }}
              transition={{ duration: 0.3 }}
            />
          )}
        </div>

        {/* Floating label */}
        <AnimatePresence>
          {label && (
            <motion.div
              className="absolute left-8 top-0 whitespace-nowrap bg-card border border-border rounded-md px-2 py-1 shadow-lg"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.2 }}
            >
              <span className="text-xs font-medium text-foreground">{label}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
